// src/components/expediente/EvolucionClinica.js
// =========================================================================
// 📈 EVOLUCIÓN DEL PACIENTE
// Una gráfica por cada dato numérico de la ficha (dolor EVA, peso, IMC,
// perímetro de cintura…) a lo largo de las visitas. Los datos salen de
// GET /consultas/evolucion/:pacienteId (mismo control de acceso que el
// historial clínico).
// =========================================================================
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import api from '../../services/api';

function Tendencia({ serie }) {
    const { puntos, campo } = serie;
    if (puntos.length < 2) return null;
    const primero = puntos[0].valor;
    const ultimo = puntos[puntos.length - 1].valor;
    const diferencia = Math.round((ultimo - primero) * 10) / 10;
    if (diferencia === 0) return <span className="text-xs font-bold text-slate-400">Sin cambios</span>;

    // En el dolor, bajar es bueno; en el resto solo informamos del sentido
    const mejora = campo === 'eva' ? diferencia < 0 : null;
    const color = mejora === null ? 'text-slate-600 bg-slate-100' : mejora ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50';
    return (
        <span className={`px-2 py-0.5 rounded-md text-xs font-black ${color}`}>
            {diferencia > 0 ? '▲' : '▼'} {Math.abs(diferencia)}{serie.unidad ? ` ${serie.unidad}` : ''} desde la 1.ª visita
        </span>
    );
}

function Grafica({ serie }) {
    const datos = serie.puntos.map(p => ({ ...p, etiqueta: format(new Date(p.fecha), 'd MMM yy', { locale: es }) }));
    const ultimo = serie.puntos[serie.puntos.length - 1];
    const dominio = serie.tipo === 'escala' && serie.min !== null && serie.max !== null ? [serie.min, serie.max] : ['auto', 'auto'];

    return (
        <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: serie.color }}>{serie.nombreEspecialidad}</p>
                    <h4 className="text-base font-black text-slate-900">{serie.etiqueta}</h4>
                </div>
                <div className="text-right">
                    <p className="text-2xl font-black text-slate-900 leading-none">
                        {ultimo.valor}<span className="text-sm font-bold text-slate-400">{serie.unidad ? ` ${serie.unidad}` : serie.tipo === 'escala' ? ` / ${serie.max}` : ''}</span>
                    </p>
                    <p className="text-[11px] font-semibold text-slate-400 mt-1">Último registro · {format(new Date(ultimo.fecha), "d 'de' MMMM", { locale: es })}</p>
                </div>
            </div>
            <div className="mb-3"><Tendencia serie={serie} /></div>
            {serie.puntos.length >= 2 ? (
                <div className="h-48" role="img" aria-label={`Gráfica de ${serie.etiqueta}: ${serie.puntos.map(p => p.valor).join(', ')}`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={datos} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: '#64748b' }} />
                            <YAxis domain={dominio} tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals />
                            <Tooltip formatter={(v) => [`${v}${serie.unidad ? ` ${serie.unidad}` : ''}`, serie.etiqueta]} />
                            <Line type="monotone" dataKey="valor" stroke={serie.color || '#2563eb'} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} isAnimationActive={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <p className="text-xs font-semibold text-slate-400">Con dos o más visitas aparecerá la gráfica.</p>
            )}
        </div>
    );
}

export default function EvolucionClinica({ pacienteId }) {
    const [series, setSeries] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let vigente = true;
        setSeries(null);
        api.get(`/consultas/evolucion/${pacienteId}`)
            .then(res => { if (vigente) setSeries(Array.isArray(res) ? res : []); })
            .catch(err => { if (vigente) { setError(err.message); setSeries([]); } });
        return () => { vigente = false; };
    }, [pacienteId]);

    if (series === null) {
        return <div className="h-48 bg-white border border-slate-200 rounded-3xl animate-pulse" />;
    }
    if (error) {
        return <div className="p-6 bg-rose-50 border border-rose-200 rounded-3xl text-sm font-semibold text-rose-700">{error}</div>;
    }
    if (!series.length) {
        return (
            <div className="p-8 bg-white border border-slate-200 rounded-3xl text-center">
                <p className="text-sm font-black text-slate-700">Aún no hay datos para graficar</p>
                <p className="text-xs font-semibold text-slate-400 mt-1">Aparecerán al registrar en la ficha datos como el dolor (EVA), el peso o el IMC.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {series.map(serie => <Grafica key={serie.clave} serie={serie} />)}
        </div>
    );
}
